"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import FilterSearch from "../../../../components/FilterSearch(name)";
import Button from "../../../../components/Button";
import Pagination from "../../../../components/Pagination";
import styles from "../css/consultations.module.css";
import { getPendingConsultations } from "../../../../api/clinic/consultationApi.js";
import ServiceSelectionModal from "../components/ServiceSelectionModal";
import { useConsultation } from "../../../../context/ConsultationContext";

// Helper to safely extract YYYY-MM-DD from various date formats
const extractDateISO = (val) => {
  if (!val) return "";
  try {
    const strVal = String(val);
    if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) return strVal;

    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (strVal.length >= 10 && strVal.includes('-')) {
      return strVal.slice(0, 10);
    }
  } catch (e) {
    console.error('[extractDateISO] Failed to parse:', val, e);
  }
  return "";
};

const formatDateDDMMYYYY = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

// Helper to extract HH:mm:ss from various time formats
const extractTimeHMS = (val) => {
  if (!val) return "";
  try {
    const strVal = String(val);
    if (strVal.includes('T')) {
      const d = new Date(strVal);
      if (!isNaN(d.getTime())) {
        const h = String(d.getUTCHours()).padStart(2, '0');
        const m = String(d.getUTCMinutes()).padStart(2, '0');
        const s = String(d.getUTCSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
      }
    }
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(strVal)) {
      const parts = strVal.split(':');
      const h = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const s = (parts[2] || '00').padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
  } catch (e) {
    console.error('[extractTimeHMS] Failed to parse:', val, e);
  }
  return "";
};

const toUTCDateTimeMs = (dateStr, timeStr) => {
  try {
    if (!dateStr) return Number.MIN_SAFE_INTEGER;
    const [y, m, d] = String(dateStr).split('-').map((n) => parseInt(n, 10));
    let hh = 0, mm = 0, ss = 0;
    if (timeStr) {
      const parts = String(timeStr).split(':');
      hh = parseInt(parts[0], 10) || 0;
      mm = parseInt(parts[1], 10) || 0;
      ss = parseInt(parts[2], 10) || 0;
    }
    return Date.UTC(y, (m || 1) - 1, d || 1, hh, mm, ss, 0);
  } catch {
    return Number.MIN_SAFE_INTEGER;
  }
};

export default function Consultations() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const consultationsPerPage = 4;

  // Fetch pending consultations on component mount
  useEffect(() => {
    let cancelled = false;
    const fetchConsultations = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getPendingConsultations();
        const mapped = Array.isArray(data)
          ? data.map((appt) => {
              let dateIso = extractDateISO(appt.preferreddate);
              if (!dateIso || dateIso === "1970-01-01") {
                const ca = appt.createdat || appt.createdAt;
                if (ca) dateIso = extractDateISO(ca);
              }
              const timeStr = extractTimeHMS(appt.preferredtime);
              const dateDisplay = formatDateDDMMYYYY(dateIso);
              const name = `${appt.patient?.firstname || ""} ${appt.patient?.surname || ""}`.trim();
              return {
                id: appt.appointmentid,
                appointmentId: appt.appointmentid,
                patientId: appt.patientid,
                dependentCode: appt.patient?.dependentcode || null,
                name,
                patient: appt.patient,
                service: appt.service?.servicename || "",
                serviceId: appt.serviceid,
                dateTime: `${dateDisplay} - ${timeStr}`,
                date: dateIso,
                time: timeStr,
                status: appt.status,
              };
            })
          : [];
        if (!cancelled) setConsultations(mapped);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load consultations");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConsultations();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredConsultations = consultations.filter((consult) => {
    // Apply search filter to all appointments
    const matchesSearch = !searchTerm || 
      consult.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consult.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consult.dateTime.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const sortedConsultations = filteredConsultations.slice().sort((a, b) =>
    toUTCDateTimeMs(a.date, a.time) - toUTCDateTimeMs(b.date, b.time)
  );

  const totalPages = Math.ceil(sortedConsultations.length / consultationsPerPage);
  const paginatedConsultations = sortedConsultations.slice(
    (currentPage - 1) * consultationsPerPage,
    currentPage * consultationsPerPage
  );

  const navigate = useNavigate();
  const { startConsultation } = useConsultation();

  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedConsult, setSelectedConsult] = useState(null);

  const handleConsult = (consult) => {
    setSelectedConsult(consult);
    setShowServiceModal(true);
  };

  const handleCloseModal = () => {
    setShowServiceModal(false);
    setSelectedConsult(null);
  };

  const handleProceedToConsultation = ({ serviceid, serviceCode, serviceName }) => {
    if (!selectedConsult) return;

    const normalizedCode = String(serviceCode || "").trim().toUpperCase();
    const normalizedName = String(serviceName || selectedConsult.service || "").trim().toLowerCase();

    const serviceRouteByCode = {
      "BP": "/customer/clinic/blood-pressure-test",
      "GLUC": "/customer/clinic/glucose-test",
      "CHOLES": "/customer/clinic/cholesterol-test",
      "LIPOGRAM": "/customer/clinic/lipogram-test",
      "PEAK FLOW": "/customer/clinic/peak-flow-test",
      "URINE": "/customer/clinic/urine-dipstick-test",
      "IRON": "/customer/clinic/hemoglobin-test",
      "HIV": "/customer/clinic/hiv-test",
      "EAR": "/customer/clinic/ear-syringing",
      "WOUND CARE 1": "/customer/clinic/wound-care",
      "WOUND CARE 2": "/customer/clinic/wound-care",
      "ADMIN": "/customer/clinic/injection",
      "ADMIN PEAK": "/customer/clinic/injection",
      "NEBULISE": "/customer/clinic/nebulisation",
      "PAP SMEAR": "/customer/clinic/pap-smear",
      "FAMILY PLANNING": "/customer/clinic/family-planning",
      "BABY ADMIN": "/customer/clinic/baby-immunization",
      "CONSULT": "/customer/clinic/diet-consult-weekly",
      "DIET PLUS": "/customer/clinic/diet-plus-injection",
      "DIABETES": "/customer/clinic/diabetes-consult",
      "HRA CASH": "/customer/clinic/health-risk-assessment",
      "MENOPAUSE": "/customer/clinic/metabolic-consult",
      "MOMENTUM HRA": "/customer/clinic/momentum-hra",
      "VITALITY CASH": "/customer/clinic/discovery-vitality",
      "VITALITY MA": "/customer/clinic/discovery-vitality",
    };

    const routeForService =
      serviceRouteByCode[normalizedCode] ||
      (normalizedName.includes("blood pressure") ? "/customer/clinic/blood-pressure-test" : null) ||
      (normalizedName.includes("glucose") ? "/customer/clinic/glucose-test" : null) ||
      (normalizedName.includes("cholesterol") ? "/customer/clinic/cholesterol-test" : null) ||
      (normalizedName.includes("lipogram") ? "/customer/clinic/lipogram-test" : null) ||
      (normalizedName.includes("peak flow") ? "/customer/clinic/peak-flow-test" : null) ||
      (normalizedName.includes("urine dipstick") ? "/customer/clinic/urine-dipstick-test" : null) ||
      (normalizedName.includes("haemoglobin") || normalizedName.includes("hemoglobin") ? "/customer/clinic/hemoglobin-test" : null) ||
      (normalizedName.includes("hiv") ? "/customer/clinic/hiv-test" : null) ||
      (normalizedName.includes("ear syringing") ? "/customer/clinic/ear-syringing" : null) ||
      (normalizedName.includes("wound care") ? "/customer/clinic/wound-care" : null) ||
      (normalizedName.includes("nebul") ? "/customer/clinic/nebulisation" : null) ||
      (normalizedName.includes("pap smear") ? "/customer/clinic/pap-smear" : null) ||
      (normalizedName.includes("family planning") ? "/customer/clinic/family-planning" : null) ||
      (normalizedName.includes("baby immun") || normalizedName.includes("immunisation") || normalizedName.includes("immunization")
        ? "/customer/clinic/baby-immunization"
        : null) ||
      (normalizedName.includes("diet consult") ? "/customer/clinic/diet-consult-weekly" : null) ||
      (normalizedName.includes("diet plus") ? "/customer/clinic/diet-plus-injection" : null) ||
      (normalizedName.includes("diabetes") ? "/customer/clinic/diabetes-consult" : null) ||
      (normalizedName.includes("health risk") ? "/customer/clinic/health-risk-assessment" : null) ||
      (normalizedName.includes("metabolic") || normalizedName.includes("menopause") ? "/customer/clinic/metabolic-consult" : null) ||
      (normalizedName.includes("momentum") || normalizedName.includes("multiply hra") ? "/customer/clinic/momentum-hra" : null) ||
      (normalizedName.includes("discovery vitality") ? "/customer/clinic/discovery-vitality" : null) ||
      (normalizedName.includes("wellness screening") ? "/customer/clinic/wellness-screening" : null);

    const isInjectionService =
      normalizedCode === "ADMIN" ||
      normalizedCode === "ADMIN PEAK" ||
      normalizedName.includes("injection");

    const isDietPlusInjectionService = normalizedCode === "DIET PLUS" || normalizedName.includes("diet plus");

    const isCardiometabolicScreeningService =
      normalizedCode === "HIV" ||
      normalizedCode === "DIABETES" ||
      normalizedCode === "HEART" ||
      normalizedCode === "STROKE" ||
      normalizedName.includes("hiv") ||
      normalizedName.includes("diabetes") ||
      normalizedName.includes("heart disease") ||
      normalizedName.includes("stroke");

    const isAdultVaccinationService =
      normalizedCode === "VACC ADULT" ||
      normalizedName.includes("vaccination adult") ||
      normalizedName.includes("vaccination (18") ||
      normalizedName.includes("18 years");

    const isMinorVaccinationService =
      normalizedCode === "VACC MINOR" ||
      normalizedCode === "BABY ADMIN" ||
      normalizedName.includes("minor vaccination") ||
      normalizedName.includes("vaccination minor") ||
      normalizedName.includes("baby immunisation") ||
      normalizedName.includes("baby immunization") ||
      normalizedName.includes("immunisation administration") ||
      normalizedName.includes("immunization administration") ||
      normalizedName.includes("minor");

    const isFamilyPlanningService =
      normalizedCode === "FAMILY PLANNING" ||
      normalizedName.includes("family planning");
    
    // Start consultation mode with context
    startConsultation({
      appointmentId: selectedConsult.appointmentId,
      patientId: selectedConsult.patientId,
      patient: selectedConsult.patient,
      serviceId: serviceid,
      serviceName: serviceName || selectedConsult.service,
      serviceCode: serviceCode,
      dependentCode: selectedConsult.dependentCode,
      appointmentDate: selectedConsult.date,
      appointmentTime: selectedConsult.time,
    });

    if (isInjectionService) {
      navigate("/customer/clinic/consent/injection-administration", {
        state: { continueTo: routeForService || "/customer/clinic/injection" },
      });
      return;
    }

    if (isDietPlusInjectionService) {
      navigate("/customer/clinic/consent/injection-administration", {
        state: { continueTo: routeForService || "/customer/clinic/diet-plus-injection" },
      });
      return;
    }

    if (isCardiometabolicScreeningService) {
      navigate("/customer/clinic/consent/cardiometabolic-screening", {
        state: { continueTo: routeForService || "/customer/clinic/patient-overview" },
      });
      return;
    }

    if (isAdultVaccinationService) {
      navigate("/customer/clinic/consent/vaccination-adult", {
        state: { continueTo: routeForService || "/customer/clinic/patient-overview" },
      });
      return;
    }

    if (isMinorVaccinationService) {
      navigate("/customer/clinic/consent/vaccination-minor", {
        state: { continueTo: routeForService || "/customer/clinic/baby-immunization" },
      });
      return;
    }

    if (isFamilyPlanningService) {
      navigate("/customer/clinic/consent/family-planning", {
        state: { continueTo: routeForService || "/customer/clinic/family-planning" },
      });
      return;
    }

    if (routeForService) {
      navigate(routeForService);
      return;
    }

    // Navigate to consultation details page
    navigate('/customer/clinic/patient-overview');
  };

  const handleHistory = (consult) => {
    navigate(`/customer/clinic/consultation-history/${consult.patientId}/services-done`);
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <h1 className={styles.title}>Clinic Consultations</h1>

          <div className={styles.searchBarRow}>
            <FilterSearch
              filterName="Filter"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by name, service, or date..."
            />
            {/* <Button variant="primary" onClick={handleAddPatient}>+ Add Patient</Button> */}
          </div>

          {loading && (
            <div className={styles.loadingMessage}>Loading consultations...</div>
          )}

          {error && (
            <div className={styles.errorMessage}>{error}</div>
          )}

          {!loading && !error && paginatedConsultations.length === 0 && (
            <div className={styles.emptyMessage}>No consultations scheduled for today.</div>
          )}

          {!loading && !error && paginatedConsultations.length > 0 && (
            <div className={styles.consultationsContainer}>
              {paginatedConsultations.map((consult, index) => (
                <div key={index} className={styles.consultationCard}>
                  <div className={styles.patientName}>{consult.name}</div>
                  <div className={styles.dateTimeContainer}>
                    <span className={styles.dateTime}>
                      {consult.service} {consult.dateTime}
                    </span>
                    <div className={styles.buttons}>
                      <Button className={styles.consultBtn} variant="primary" onClick={() => handleConsult(consult)}>Consult</Button>
                      <Button className={styles.historyBtn} variant="primary" onClick={() => handleHistory(consult)}>Consultations History</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}

          {/* Service Selection Modal */}
          <ServiceSelectionModal
            isOpen={showServiceModal}
            onClose={handleCloseModal}
            onProceed={handleProceedToConsultation}
            defaultServiceName={selectedConsult?.service || ""}
          />
        </main>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}